import { useEffect, useRef, useState } from "react";

import { useApiCall } from "@framework/react-hooks";

import {
  TRAIN_ROTATION_DIRECTION,
  type ITrainDetailsRotationResponse,
} from "../../scheme/components/analytics/interfaces";
import { ROTATION_DIRECTION } from "../types";

export const mapTrainRotationToToolbar = (
  value: TRAIN_ROTATION_DIRECTION | null | undefined,
): ROTATION_DIRECTION | null => {
  switch (value) {
    case TRAIN_ROTATION_DIRECTION.COUNTER_CLOCKWISE:
      return ROTATION_DIRECTION.COUNTER_CLOCKWISE;
    case TRAIN_ROTATION_DIRECTION.CLOCKWISE:
      return ROTATION_DIRECTION.CLOCKWISE;
    default:
      return null;
  }
};

interface IUseTrainRotationDirectionArgs {
  enabled: boolean;
  trainId: number | null;
}

interface ITrainRotationDirectionState {
  trainId: number;
  direction: TRAIN_ROTATION_DIRECTION;
  isLoading: boolean;
}

export const useTrainRotationDirection = ({ enabled, trainId }: IUseTrainRotationDirectionArgs) => {
  const [state, setState] = useState<ITrainRotationDirectionState | null>(null);

  const { fn: fetchRotationDirection } = useApiCall(
    (api, { idTrain }: { idTrain: number }): Promise<TRAIN_ROTATION_DIRECTION> => {
      return (
        api.fetchJson({
          url: `/trains/${idTrain}/details`,
          method: "GET",
        }) as Promise<ITrainDetailsRotationResponse[]>
      ).then(json => json[0]?.RotationDirection ?? TRAIN_ROTATION_DIRECTION.UNDEFINED);
    },
    { success: false },
  );

  const fetchRotationDirectionRef = useRef(fetchRotationDirection);

  useEffect(() => {
    fetchRotationDirectionRef.current = fetchRotationDirection;
  }, [fetchRotationDirection]);

  useEffect(() => {
    if (!enabled || trainId === null) {
      setState(null);
      return;
    }

    let cancelled = false;
    setState({ trainId, direction: TRAIN_ROTATION_DIRECTION.UNDEFINED, isLoading: true });

    void fetchRotationDirectionRef
      .current(undefined, { idTrain: trainId })
      .then(direction => {
        if (!cancelled) {
          setState({ trainId, direction: direction ?? TRAIN_ROTATION_DIRECTION.UNDEFINED, isLoading: false });
        }
      })
      .catch(error => {
        if (!cancelled) {
          console.error(error);
          setState({ trainId, direction: TRAIN_ROTATION_DIRECTION.UNDEFINED, isLoading: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, trainId]);

  const currentState = enabled && state?.trainId === trainId ? state : null;
  const trainRotationDirection = currentState?.direction ?? TRAIN_ROTATION_DIRECTION.UNDEFINED;
  const isLoading = enabled && trainId !== null && (!currentState || currentState.isLoading);
  const lockedToolbarDirection = mapTrainRotationToToolbar(trainRotationDirection);

  return {
    trainRotationDirection,
    lockedToolbarDirection,
    isRotationDirectionLocked: lockedToolbarDirection !== null,
    isLoading,
  };
};
